import { apiClient } from "./client";
import type { B2BSuppliersDatasetResponse } from "../../pages/b2b/suppliers/suppliersTypes";

export type B2BSuppliersDatasetParams = {
    period?: string;
    dateFrom?: string;
    dateTo?: string;
    year?: number;
    months?: number[];
};

export async function fetchB2BSuppliersDataset(params: B2BSuppliersDatasetParams = {}) {
    const response = await apiClient.get<B2BSuppliersDatasetResponse>("/b2b/suppliers", {
        params: {
            ...(params.period ? { period: params.period } : {}),
            ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
            ...(params.dateTo ? { date_to: params.dateTo } : {}),
            ...(params.year ? { year: params.year } : {}),
            ...(params.months?.length ? { months: params.months.join(",") } : {}),
        },
    });

    return response.data;
}

export type SupplierDossierParams = {
    supplierKey: string;
    supplierId?: number;
    supplierName?: string;
};

export type SupplierDossierResponse = {
    supplierKey: string;
    supplierId: number;
    name: string;
    partner: {
        company: string;
        eik: string;
        phone: string;
        email: string;
        city: string;
        address: string;
        mol: string;
        deferred_payment?: string;
        storage?: string;
        source?: "crm" | "erp" | "fallback";
    };
    crmReady?: boolean;
    hasCrmProfile?: boolean;
    buyer: {
        code: string;
        name: string;
        assignedAt: string | null;
        assignedByName: string | null;
    } | null;
    bible: {
        id: number;
        friendlyName: string;
        type?: string | null;
        currency?: string | null;
        deliveryPeriodDays?: string | null;
        transport?: string | null;
        transportPeriodDays?: string | null;
        executionPeriodDays?: string | null;
        storagePeriodDays?: string | null;
        payType?: string | null;
        contactPerson?: string | null;
        contactEmail?: string | null;
        contact?: string | null;
        phone?: string | null;
        comment?: string | null;
        registeredName?: string | null;
        supplierName?: string | null;
        discountPct?: string | null;
        minimalQuantity?: string | null;
        labelImport?: string | null;
        labelBarcode?: string | null;
        expensePct?: string | null;
        taxMitoPct?: string | null;
        bonusTurnover?: string | null;
    } | null;
    recentDeliveries: Array<{
        id: number;
        acct: string;
        eventDate: string;
        eventDateBg: string;
        total: number;
        quantity: number;
        managerName: string;
        statusId: number;
    }>;
    summary: {
        recentDeliveryCount: number;
        recentDeliveryValue: number;
        shownDeliveryValue12m: number;
    };
};

export async function fetchSupplierDossier(params: SupplierDossierParams) {
    const response = await apiClient.get<SupplierDossierResponse>("/b2b/suppliers/dossier", {
        params: {
            supplier_key: params.supplierKey,
            ...(params.supplierId ? { supplier_id: params.supplierId } : {}),
            ...(params.supplierName ? { supplier_name: params.supplierName } : {}),
        },
    });

    return response.data;
}

export type UpdateSupplierDossierParams = {
    supplierKey: string;
    supplierId?: number;
    supplierName?: string;
    company: string;
    eik: string;
    phone: string;
    email: string;
    city: string;
    address: string;
    mol: string;
    deferred_payment: string;
    storage: string;
};

export async function updateSupplierDossier(
    params: UpdateSupplierDossierParams,
): Promise<{ partner: SupplierDossierResponse["partner"] }> {
    // Запис на досие — временно изключен (route е закоментиран).
    void params;
    throw new Error("Редакцията на досие е временно изключена.");
    /*
    const response = await apiClient.put<{ partner: SupplierDossierResponse["partner"] }>(
        "/b2b/suppliers/dossier",
        {
            supplier_key: params.supplierKey,
            supplier_id: params.supplierId ?? 0,
            supplier_name: params.supplierName ?? "",
            company: params.company,
            eik: params.eik,
            phone: params.phone,
            email: params.email,
            city: params.city,
            address: params.address,
            mol: params.mol,
            deferred_payment: params.deferred_payment,
            storage: params.storage,
        },
    );

    return response.data;
    */
}

export type AssignSupplierBuyerParams = {
    supplierKey: string;
    supplierId?: number;
    supplierName: string;
    buyerCode: string;
    note?: string;
};

export async function assignSupplierBuyer(params: AssignSupplierBuyerParams): Promise<unknown> {
    // Назначаване на баер — временно изключено (route е закоментиран).
    void params;
    throw new Error("Назначаването на баер е временно изключено.");
    /*
    const response = await apiClient.put("/b2b/suppliers/assignment", {
        supplier_key: params.supplierKey,
        supplier_id: params.supplierId ?? 0,
        supplier_name: params.supplierName,
        buyer_code: params.buyerCode,
        ...(params.note ? { note: params.note } : {}),
    });

    return response.data;
    */
}

export async function unassignSupplierBuyer(supplierKey: string, note?: string): Promise<unknown> {
    // Премахване на баер — временно изключено (route е закоментиран).
    void supplierKey;
    void note;
    throw new Error("Премахването на баер е временно изключено.");
    /*
    const response = await apiClient.delete("/b2b/suppliers/assignment", {
        data: {
            supplier_key: supplierKey,
            ...(note ? { note } : {}),
        },
    });

    return response.data;
    */
}

export type B2BSuppliersMotionByCodeParams = {
    articleCode: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
};

export type B2BSuppliersMotionByCodeResponse = {
    article: { goodId: number; code: string; name: string } | null;
    period: { from: string; to: string };
    summary: {
        firstDate: string | null;
        lastDate: string | null;
        deliveryCount: number;
        totalQty: number;
    } | null;
    rows: Array<{
        eventDate: string;
        deliveryId: number;
        acct: string;
        qty: number;
        qtyScanned: number;
        supplierId: number;
        supplierName: string;
        managerName: string;
        statusId: number;
    }>;
    meta: { rowCount: number; found: boolean };
};

export async function fetchB2BSuppliersMotionByCode(params: B2BSuppliersMotionByCodeParams) {
    const response = await apiClient.get<B2BSuppliersMotionByCodeResponse>("/b2b/suppliers/motion-by-code", {
        params: {
            article_code: params.articleCode,
            ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
            ...(params.dateTo ? { date_to: params.dateTo } : {}),
            ...(params.limit ? { limit: params.limit } : {}),
        },
    });

    return response.data;
}

export type B2BSuppliersOurBrandArticle = {
    goodId: number;
    articleCode: string;
    articleName: string;
    barcode: string;
    groupName: string;
    paramValue: string;
    /** Base.dbo.Goods.Catalog2 — име на доставчик/каталог */
    catalog2: string;
    supplierName: string;
};

export type B2BSuppliersOurBrandResponse = {
    articles: B2BSuppliersOurBrandArticle[];
    meta: { articleCount: number };
};

export async function fetchB2BSuppliersOurBrand() {
    const response = await apiClient.get<B2BSuppliersOurBrandResponse>("/b2b/suppliers/our-brand");

    return response.data;
}

export type B2BSuppliersStorageReportRow = {
    supplierKey: string;
    supplierId: number;
    company: string;
    eik: string;
    city: string;
    storage: string;
    deferred_payment: string;
    phone: string;
    email: string;
    mol: string;
    updatedAt: string | null;
    bibleId?: number | null;
    bibleStoragePeriodDays?: string | null;
};

export type B2BSuppliersStorageReportResponse = {
    rows: B2BSuppliersStorageReportRow[];
    meta: {
        total: number;
        withStorage: number;
        withoutStorage: number;
        withBibleStorage?: number;
        bibleReady?: boolean;
    };
    lastUpdatedAt: string;
};

export async function fetchB2BSuppliersStorageReport(search?: string) {
    const response = await apiClient.get<B2BSuppliersStorageReportResponse>("/b2b/suppliers/storage-report", {
        params: search?.trim() ? { search: search.trim() } : {},
    });

    const payload = response.data;

    return {
        ...payload,
        rows: Array.isArray(payload?.rows) ? payload.rows : [],
        meta: payload?.meta ?? { total: 0, withStorage: 0, withoutStorage: 0 },
    };
}

export type B2BBibleSupplierNameRow = {
    id: number;
    friendlyName: string;
};

export async function fetchB2BSuppliersBibleNames(query = "") {
    const response = await apiClient.get<{ rows: B2BBibleSupplierNameRow[] }>("/b2b/suppliers/bible-names", {
        params: query.trim() ? { q: query.trim() } : {},
    });

    const rows = response.data?.rows;

    return Array.isArray(rows) ? rows : [];
}

export type B2BSuppliersStockReportParams = {
    period?: string;
    dateFrom?: string;
    dateTo?: string;
    year?: number;
    months?: number[];
    /** Good ID-та, които задължително да влязат в резултата (сравнение на периоди). */
    ensureGoodIds?: number[];
    /** Филтър по Base.dbo.Goods.Catalog3 (сървърно). */
    supplierName?: string;
    /** Търсене по име — сървърно, вкл. нулеви количества. */
    articleName?: string;
    /** Търсене по код — сървърно, вкл. нулеви количества. */
    articleCode?: string;
};

export type B2BSuppliersStockReportNode = {
    nodeType: "group" | "article";
    groupId: number;
    groupName: string;
    groupCode: string;
    articleCode: string;
    articleName: string;
    stockGroupName: string;
    supplierId?: number;
    bibleSupplierId?: number;
    supplierName?: string;
    /** Base.dbo.Goods.Catalog3 — реален източник за доставчик/контейнер. */
    catalog3?: string;
    /** Base.dbo.Goods.Catalog2 */
    catalog2?: string;
    barcode: string;
    /** Base.dbo.Goods.PriceIn */
    deliveryPrice?: number;
    /** Цена от последния складов документ за доставка (warehouse_delivery_product.pricein). */
    lastDeliveryPrice?: number;
    /** Base.dbo.Goods.PriceOut2 (текуща продажна) */
    lastSalePrice?: number;
    openingQty: number;
    deliveriesQty: number;
    salesQty: number;
    /** Доставна стойност на доставките в периода (qty × PriceIn от операции). */
    deliveriesCost?: number;
    /** Доставна стойност на продажбите в периода (qty × PriceIn от операции). */
    salesCost?: number;
    stornoQty: number;
    writeOffQty: number;
    defectsQty: number;
    reservedQty: number;
    closingQty: number;
    /** Expo наличност в момента на заявката (склад − picked). */
    currentQty?: number;
    /** Резерв в отворени колички към момента на заявката (Semaphor qtty). */
    reservedNowQty?: number;
    /** Доставна стойност на текущата наличност (currentQty × lastDeliveryPrice). */
    stockDeliveryValue?: number;
    locked: boolean;
    /** InWarehouse.warehouse_goods_box.msr — междинна тр. опаковка */
    transportPackMidQty?: number;
    /** InWarehouse.warehouse_goods_box.msr1 — крайна тр. опаковка */
    transportPackFinalQty?: number;
    transportPackQty: number;
    lowStock: boolean;
    lowStockHint: string | null;
    children: B2BSuppliersStockReportNode[];
};

export type B2BSuppliersStockReportResponse = {
    period: { from: string; to: string; label: string; days: number; period: string };
    tree: B2BSuppliersStockReportNode[];
    meta: {
        articleCount: number;
        lowStockCount: number;
        buyerScope?: "all" | "filtered";
        scopedBuyerCodes?: string[] | null;
        scopeGoodCount?: number | null;
        truncated?: boolean;
        articleLimit?: number | null;
    };
};

export async function fetchB2BSuppliersStockReport(params: B2BSuppliersStockReportParams = {}) {
    const response = await apiClient.get<B2BSuppliersStockReportResponse>("/b2b/suppliers/stock-report", {
        params: {
            ...(params.period ? { period: params.period } : {}),
            ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
            ...(params.dateTo ? { date_to: params.dateTo } : {}),
            ...(params.year ? { year: params.year } : {}),
            ...(params.months?.length ? { months: params.months.join(",") } : {}),
            ...(params.supplierName?.trim() ? { supplier_name: params.supplierName.trim() } : {}),
            ...(params.articleName?.trim() ? { article_name: params.articleName.trim() } : {}),
            ...(params.articleCode?.trim() ? { article_code: params.articleCode.trim() } : {}),
        },
    });

    const payload = response.data;
    return {
        ...payload,
        period: payload?.period ?? { from: "", to: "", label: "Справки", days: 0, period: "" },
        tree: Array.isArray(payload?.tree) ? payload.tree : [],
        meta: payload?.meta ?? { articleCount: 0, lowStockCount: 0 },
    };
}

export type B2BSuppliersAvailabilityMetric = {
    goodId: number;
    salesQty: number;
    openingQty: number;
    closingQty: number;
};

export async function fetchB2BSuppliersAvailabilityMetrics(params: {
    dateFrom: string;
    dateTo: string;
    goodIds: number[];
}) {
    const goodIds = params.goodIds.map((id) => Number(id)).filter((id) => id > 0).slice(0, 2500);
    if (!params.dateFrom || !params.dateTo || goodIds.length === 0) {
        return [] as B2BSuppliersAvailabilityMetric[];
    }

    const response = await apiClient.post<{ metrics: B2BSuppliersAvailabilityMetric[] }>(
        "/b2b/suppliers/stock-report/availability",
        {
            date_from: params.dateFrom,
            date_to: params.dateTo,
            good_ids: goodIds,
        },
    );

    return Array.isArray(response.data?.metrics) ? response.data.metrics : [];
}
